import { useState, useEffect } from "react";
import { Box, Typography, TextField, FormControl } from "@mui/material";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "../utils/firebase-config"; // Adjust path if necessary
import MyGoogleMap from "@/components/Elements/GoogleMap";
import moment from "moment";

// Helper function to get the start and end of a given day in UTC
const getStartAndEndOfDay = (date) => {
  const startOfDay = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      5,
      0,
      0,
      0
    )
  );
  const endOfDay = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      28,
      59,
      59,
      999
    )
  );
  return {
    startTimestamp: Timestamp.fromDate(startOfDay),
    endTimestamp: Timestamp.fromDate(endOfDay),
  };
};

// Helper function to serialize any Firestore GeoPoint data
const serializeGeoPoint = (geoPoint) => ({
  latitude: geoPoint.latitude,
  longitude: geoPoint.longitude,
});

// Helper function to serialize Firestore Timestamp to an ISO string
const serializeTimestamp = (timestamp) => timestamp.toDate().toISOString();

// Recursive function to ensure all fields in a document are JSON-serializable
const serializeFirestoreData = (data) => {
  const serializedData = {};

  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Timestamp) {
      serializedData[key] = serializeTimestamp(value);
    } else if (
      value &&
      value.latitude !== undefined &&
      value.longitude !== undefined
    ) {
      // Assuming any object with latitude and longitude is a GeoPoint
      serializedData[key] = serializeGeoPoint(value);
    } else if (typeof value === "object" && value !== null) {
      // Recursively serialize nested objects
      serializedData[key] = serializeFirestoreData(value);
    } else {
      // Copy the value directly if it's already JSON-serializable
      serializedData[key] = value;
    }
  }

  return serializedData;
};

// Server-side data fetching
export async function getServerSideProps(context) {
  const { date } = context.query;
  const selectedDate = date ? new Date(date) : new Date(); // Default to today if no date is specified

  const { startTimestamp, endTimestamp } = getStartAndEndOfDay(selectedDate);
  const eventsCollection = collection(db, "events");
  const dateQuery = query(
    eventsCollection,
    where("main_date", ">=", startTimestamp),
    where("main_date", "<=", endTimestamp),
    orderBy("main_date", "asc")
  );

  const querySnapshot = await getDocs(dateQuery);
  const initialEvents = querySnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...serializeFirestoreData(data), // Use recursive serialization for each document
    };
  });

  return {
    props: {
      initialEvents,
      initialDate: selectedDate.toISOString(),
    },
  };
}

// Custom Date Filter Component
const DateFilter = ({ onChange, value = "" }) => (
  <FormControl sx={{ mb: 2 }} component="fieldset">
    <TextField
      fullWidth
      label="Select Date"
      type="date"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      InputLabelProps={{
        shrink: true,
      }}
      sx={{ mb: 2, width: { xs: "100%", sm: "300px" } }}
    />
  </FormControl>
);

// Main component for EventsMapPage
const EventsMapPage = ({ initialEvents, initialDate }) => {
  const [selectedDate, setSelectedDate] = useState(initialDate.split("T")[0]); // ISO format to 'YYYY-MM-DD'
  const [events, setEvents] = useState(initialEvents);
  const [loading, setLoading] = useState(false);

  // Fetch events when date is changed
  const fetchEventsByDate = async (date) => {
    setLoading(true);
    const parsedDate = new Date(date);
    const { startTimestamp, endTimestamp } = getStartAndEndOfDay(parsedDate);

    const eventsCollection = collection(db, "events");
    const dateQuery = query(
      eventsCollection,
      where("main_date", ">=", startTimestamp),
      where("main_date", "<=", endTimestamp),
      orderBy("main_date", "asc")
    );

    try {
      const querySnapshot = await getDocs(dateQuery);
      const fetchedEvents = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...serializeFirestoreData(doc.data()), // Use recursive serialization
      }));
      setEvents(fetchedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  // Refetch events when date changes
  useEffect(() => {
    fetchEventsByDate(selectedDate);
  }, [selectedDate]);

  return (
    <Box sx={{ padding: { xs: 0, sm: 3 }, margin: "0 auto" }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{ fontWeight: "bold", textAlign: "center", mb: 2 }}
      >
        Events Map View
      </Typography>

      {/* Custom Date Picker */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "start" }}>
        <DateFilter
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)} // Pass date string directly
        />
      </Box>

      {/* Map Component */}
      {loading ? (
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          Loading events...
        </Typography>
      ) : (
        <MyGoogleMap
          eventView={true}
          height="600px"
          showDistance={false}
          markers={events.map((event) => ({
            ...event,
            description: event.eventdescription,
            address1: event.address,
            venue_city: event.city,
            VENUE_ZIP: event.zip,
            main_date: moment(event.main_date).format("YYYY-MM-DD hh:mm:ss A"),
            link: `/venues?internalLink=${
              event.venueInternalLink || event.venueId
            }`,
          }))}
        />
      )}
    </Box>
  );
};

export default EventsMapPage;
